import { EChartsOption } from 'echarts';
import { BoxplotChartConfig } from '../models/chart-types';
import {
  getTitleConfig,
  getAxisLabelConfig,
  getSplitLineConfig,
  getGridConfig,
  getLegendConfig,
} from '../constants/chart-theme.constants';

/**
 * Build ECharts options for boxplot chart with optional scatter overlay
 */
export function buildBoxplotOptions(config: BoxplotChartConfig): EChartsOption {
  const series: any[] = [];

  // Add boxplot series
  config.boxplotSeries.forEach((boxplotSeries) => {
    const seriesEmphasis = boxplotSeries.emphasis;
    const medianLineStyle = boxplotSeries.medianLineStyle;
    // Box width as percent of category band (narrower = smaller bars)
    const boxWidthPercent = 40;
    const halfWidth = (boxWidthPercent / 100) / 2; // 0.2 for 40% width
    // Build markLine for median segments when medianLineStyle is set (data is [min, Q1, median, Q3, max], index 2 = median)
    const markLine =
      medianLineStyle && boxplotSeries.data?.length
        ? {
            symbol: ['none', 'none'] as const,
            lineStyle: {
              color: medianLineStyle.color ?? '#39A8DC',
              width: medianLineStyle.width ?? 3,
            },
            data: boxplotSeries.data.map((row, i) => {
              const median = row[2];
              return [
                { coord: [i - halfWidth, median] },
                { coord: [i + halfWidth, median] },
              ];
            }),
          }
        : undefined;
    series.push({
      name: boxplotSeries.name,
      type: 'boxplot',
      data: boxplotSeries.data,
      color: boxplotSeries.color,
      boxWidth: [`${boxWidthPercent}%`, `${boxWidthPercent}%`],
      itemStyle: boxplotSeries.itemStyle || {
        borderWidth: 2,
      },
      ...(markLine ? { markLine } : {}),
      emphasis: {
        focus: seriesEmphasis?.focus ?? 'series',
        scale: seriesEmphasis?.scale ?? false,
        ...(seriesEmphasis ?? {}),
      },
    });
  });

  // Add scatter overlay series if provided
  if (config.scatterSeries) {
    config.scatterSeries.forEach((scatterSeries) => {
      series.push({
        name: scatterSeries.name,
        type: 'scatter',
        data: scatterSeries.data,
        color: scatterSeries.color,
        symbolSize: scatterSeries.symbolSize || 10,
        itemStyle: scatterSeries.itemStyle || {
          borderWidth: 2,
          borderColor: '#fff',
        },
        emphasis: {
          scale: 1.5,
        },
        z: 10, // Ensure scatter appears on top
      });
    });
  }

  // Add line overlay series if provided (e.g. Employee Metric over time)
  if (config.lineSeries) {
    config.lineSeries.forEach((lineSeries) => {
      const itemStyle = lineSeries.itemStyle ?? {};
      series.push({
        name: lineSeries.name,
        type: 'line',
        data: lineSeries.data,
        smooth: lineSeries.smooth ?? false,
        showSymbol: lineSeries.showSymbol !== false,
        symbol: 'circle', // filled circle (not emptyCircle)
        symbolSize: 10,
        color: lineSeries.color,
        lineStyle: lineSeries.lineStyle ?? { width: 2 },
        itemStyle: {
          color: itemStyle.color ?? lineSeries.color,
          borderColor: itemStyle.borderColor,
          borderWidth: itemStyle.borderWidth ?? 0,
          ...itemStyle,
        },
        emphasis: lineSeries.emphasis ?? { focus: 'series' },
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          color: '#B9A28B',
        },
        z: 10, // Ensure line appears on top
      });
    });
  }

  const theme = config.theme;

  const options: EChartsOption = {
    title: getTitleConfig(config.title, config.subtitle, theme),

    tooltip: {
      trigger: config.tooltip?.trigger || 'item',
      axisPointer: {
        type: 'shadow',
      },
      ...config.tooltip,
    },

    legend: getLegendConfig(config.legend, theme),

    grid: config.grid || getGridConfig(!!config.title, {
      left: '5%',
      right: '15%',
    }),

    xAxis: {
      type: 'category',
      data: config.categories,
      name: config.xAxis.name,
      boundaryGap: true,
      axisLabel: getAxisLabelConfig(config.xAxis.axisLabel, theme),
      axisLine: config.xAxis.axisLine,
      axisTick: config.xAxis.axisTick,
      splitLine: getSplitLineConfig(config.xAxis.splitLine, theme),
      ...config.xAxis,
    } as any,

    yAxis: {
      type: config.yAxis.type || 'value',
      name: config.yAxis.name,
      axisLabel: getAxisLabelConfig(config.yAxis.axisLabel, theme),
      axisLine: config.yAxis.axisLine,
      axisTick: config.yAxis.axisTick,
      splitLine: getSplitLineConfig(config.yAxis.splitLine, theme),
      ...config.yAxis,
    } as any,

    series,
  };

  // Merge with custom options if provided (merge title so built-in left: 'center' is preserved)
  if (!config.customOptions) return options;
  const merged = { ...options, ...config.customOptions };
  if (options.title && config.customOptions.title) {
    merged.title = { ...options.title, ...config.customOptions.title };
  }
  return merged;
}
